import React, { useMemo, useEffect, useState } from 'react';
import { useWeldParameters } from '../../../../../../redux/ProductionData/hooks';
import P625Parameters from './P625/P625Parameters';
import M500Parameters from './M500/M500Parameters';
import P600zParameters from './P600z/P600zParameters';
import { Dialog, DialogContent, DialogHeader } from '../../../../../../components/ui/dialog';
import { Button } from '../../../../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../components/ui/select';
import { Label } from '../../../../../../components/ui/label';
import { ArrowLeft, ChevronLeft, Loader2 } from 'lucide-react';

interface CommonWeldParametersModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  canEdit?: boolean;
}

/**
 * Common Weld Parameters Modal Component
 * 
 * This component:
 * 1. Fetches weld parameters from API via Redux
 * 2. Shows loading state while fetching
 * 3. Determines the machine type (P625 or M500) from the data
 * 4. Renders the appropriate content based on machine type
 */
const CommonWeldParametersModal: React.FC<CommonWeldParametersModalProps> = ({
  isOpen,
  onClose,
  projectId,
  canEdit = true
}) => {




  const {
    weldParameters,
    isLoading,
    error,
    refresh
  } = useWeldParameters(projectId);

  // Fetch new data when modal opens or project changes (only when modal is open)
  // Note: The hook automatically clears old data when projectId changes
  useEffect(() => {
    // Only call API when modal is actually open
    if (!isOpen) {
      return;
    }

    // Fetch data for current project
    if (projectId) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, projectId]); // Fetch when modal opens or project changes

  // Use API data
  const dataSource = weldParameters;

  // Helper function to detect machine type from parsed_json_data
  const detectMachineTypeFromData = (parsedData: any): string | null => {
    if (!parsedData || typeof parsedData !== 'object') return null;
    
    // Check for P600z: parsed_json_data is an array with Type: "PASS"
    if (Array.isArray(parsedData)) {
      const hasPassType = parsedData.some((item: any) => item.Type === 'PASS');
      if (hasPassType) {
        return 'p600z';
      }
    }
    
    // Check for P625: "WELD PARAMETERS" key exists (without PIPE PARAMETERS)
    const hasWeldParams = Object.keys(parsedData).some(key => 
      key.includes('WELD PARAMETERS') && !key.includes('PIPE PARAMETERS')
    );
    const hasPipeParams = Object.keys(parsedData).some(key => 
      key.includes('PIPE PARAMETERS')
    );
    
    if (hasWeldParams && !hasPipeParams) {
      return 'p625';
    } else if (hasPipeParams) {
      return 'm500';
    }
    
    return null;
  };

  // Group machines by type when groupedByMachine is an array
  const groupedMachines = useMemo(() => {
    if (!dataSource?.groupedByMachine) return null;

    const groupedByMachine = dataSource.groupedByMachine as any;

    // Handle array structure
    if (Array.isArray(groupedByMachine)) {
      const machines: { [key: string]: any[] } = {
        p625: [],
        m500: [],
        p600z: []
      };

      groupedByMachine.forEach((item: any) => {
        const machineType = detectMachineTypeFromData(item?.parsed_json_data);
        if (machineType && machines[machineType as keyof typeof machines]) {
          machines[machineType as keyof typeof machines].push(item);
        }
      });

      // Return only non-empty machine groups
      const result: { [key: string]: any[] } = {};
      Object.keys(machines).forEach(key => {
        if (machines[key as keyof typeof machines].length > 0) {
          result[key] = machines[key as keyof typeof machines];
        }
      });

      return Object.keys(result).length > 0 ? result : null;
    }

    // Handle object structure
    return groupedByMachine;
  }, [dataSource]);

  // Get all available machines with all their job numbers
  const availableMachines = useMemo(() => {
    if (!groupedMachines) return [];
    
    const machines: Array<{ type: string; jobNumber: string; jobData: any }> = [];
    
    // Helper to process all jobs for a machine type
    const processMachineType = (machineType: string, machineData: any[]) => {
      if (Array.isArray(machineData) && machineData.length > 0) {
        machineData.forEach((job) => {
          const jobNumber = job?.job_number || '';
          machines.push({
            type: machineType,
            jobNumber: jobNumber,
            jobData: job
          });
        });
      }
    };

    if (Array.isArray(dataSource?.groupedByMachine)) {
      // Array structure - get all jobs for each machine type
      Object.keys(groupedMachines).forEach((machineType) => {
        const machineData = (groupedMachines as any)[machineType];
        processMachineType(machineType, machineData);
      });
    } else {
      // Object structure - get all jobs for each machine type
      if (groupedMachines.p625 && Array.isArray(groupedMachines.p625)) {
        processMachineType('p625', groupedMachines.p625);
      }
      if (groupedMachines.m500 && Array.isArray(groupedMachines.m500)) {
        processMachineType('m500', groupedMachines.m500);
      }
      if (groupedMachines.p600z && Array.isArray(groupedMachines.p600z)) {
        processMachineType('p600z', groupedMachines.p600z);
      }
    }
    
    return machines;
  }, [groupedMachines, dataSource]);

  // State for selected machine/job (format: "machineType-jobNumber")
  const [selectedMachine, setSelectedMachine] = useState<string>('');
  const prevProjectIdRef = React.useRef<number | undefined>(undefined);

  // Reset selected machine when project changes (not when modal closes/reopens)
  useEffect(() => {
    if (prevProjectIdRef.current !== undefined && prevProjectIdRef.current !== projectId) {
      setSelectedMachine('');
    }
    prevProjectIdRef.current = projectId;
  }, [projectId]);

  // Compute the effective selected machine value (with fallback to first available)
  const effectiveSelectedMachine = useMemo(() => {
    if (isLoading || availableMachines.length === 0) return '';
    
    // If selectedMachine is valid, use it
    if (selectedMachine && availableMachines.some(m => `${m.type}-${m.jobNumber}` === selectedMachine)) {
      return selectedMachine;
    }
    
    // Otherwise use first available machine/job
    if (availableMachines.length > 0) {
      return `${availableMachines[0].type}-${availableMachines[0].jobNumber}`;
    }
    return '';
  }, [selectedMachine, availableMachines, isLoading]);

  // Set default machine when available machines become available and no selection exists
  useEffect(() => {
    if (availableMachines.length > 0 && !isLoading) {
      // Check if current selection is valid
      const isValidSelection = selectedMachine && availableMachines.some(
        m => `${m.type}-${m.jobNumber}` === selectedMachine
      );
      
      // If no valid selection, set default to first available machine
      if (!isValidSelection) {
        const defaultMachine = `${availableMachines[0].type}-${availableMachines[0].jobNumber}`;
        setSelectedMachine(defaultMachine);
      }
    }
  }, [availableMachines, isLoading, selectedMachine]);

  // Get the selected job data
  const selectedJobData = useMemo(() => {
    if (!selectedMachine || !availableMachines.length) {
      // If no selection but we have machines, return first one
      if (availableMachines.length > 0) {
        return availableMachines[0].jobData;
      }
      return null;
    }
    
    // Split on first '-' only (job numbers might contain dashes)
    const firstDashIndex = selectedMachine.indexOf('-');
    if (firstDashIndex === -1) {
      // If no dash, try to find by machine type only (fallback to first)
      const machine = availableMachines.find(m => m.type === selectedMachine);
      return machine?.jobData || (availableMachines.length > 0 ? availableMachines[0].jobData : null);
    }
    
    const machineType = selectedMachine.substring(0, firstDashIndex);
    const jobNumber = selectedMachine.substring(firstDashIndex + 1);
    
    const machine = availableMachines.find(m => 
      m.type === machineType && 
      String(m.jobNumber).trim() === String(jobNumber).trim()
    );
    
    return machine?.jobData || null;
  }, [selectedMachine, availableMachines]);

  // Determine current machine type (from selection or auto-detection)
  const currentMachineType = useMemo(() => {
    if (isLoading) return null; // Don't show type while loading
    
    if (selectedMachine) {
      // Split on first '-' only
      const firstDashIndex = selectedMachine.indexOf('-');
      if (firstDashIndex !== -1) {
        const machineType = selectedMachine.substring(0, firstDashIndex);
        if (availableMachines.some(m => m.type === machineType)) {
          return machineType;
        }
      }
    }
    
    // Otherwise use first available machine
    return availableMachines.length > 0 ? availableMachines[0].type : null;
  }, [selectedMachine, availableMachines, isLoading]);

  if (!isOpen) {
    return null;
  }

  // Common Dialog wrapper
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[100vw] w-screen h-screen max-h-screen p-0 gap-0 overflow-hidden flex flex-col bg-page-bg">
        <DialogHeader className="flex-shrink-0 px-6 py-4 pr-14 border-b border-[#E2E5EA] bg-white flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={onClose}
              variant="outline"
              size="sm"
              className="shrink-0 h-9 w-9 p-0 border-[#E2E5EA] hover:bg-page-bg"
              title="Close"
            >
              <ChevronLeft className="h-4 w-4 text-text-grey" />
            </Button>
            <h2 className="text-[18px] font-semibold text-blueprint-blue leading-tight">
              {isLoading ? 'Loading Weld Parameters...' :
                currentMachineType === 'p625' ? 'P625 Weld Parameters' :
                  currentMachineType === 'm500' ? 'M500 Weld Parameters' :
                    currentMachineType === 'p600z' ? 'P600z Weld Parameters' :
                      'Weld Parameters'}
            </h2>
          </div>

          {/* Machine Selection Dropdown — multiple machines */}
          {!isLoading && !error && availableMachines.length > 1 && (
            <div className="flex items-center gap-2">
              <Label className="text-[12px] font-medium text-text-grey whitespace-nowrap">
                Machine/Job:
              </Label>
              <Select
                key={`select-${projectId}-${availableMachines.length}`}
                value={selectedMachine || effectiveSelectedMachine || undefined}
                onValueChange={(value) => setSelectedMachine(value)}
              >
                <SelectTrigger className="w-[200px] h-9">
                  <SelectValue placeholder="Choose a machine..." />
                </SelectTrigger>
                <SelectContent>
                  {availableMachines.map((machine, index) => {
                    const value = `${machine.type}-${machine.jobNumber}`;
                    return (
                      <SelectItem key={`${machine.type}-${machine.jobNumber}-${index}`} value={value}>
                        {machine.type.toUpperCase()}: {machine.jobNumber || 'N/A'}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Single machine info */}
          {!isLoading && !error && availableMachines.length === 1 && (
            <div className="flex items-center gap-2">
              <span className="text-[12px] font-medium text-heritage-grey">Machine/Job:</span>
              <span className="text-[13px] font-semibold text-blueprint-blue">
                {availableMachines[0].type.toUpperCase()}: {availableMachines[0].jobNumber || 'N/A'}
              </span>
            </div>
          )}
        </DialogHeader>

        {/* Loading State */}
        {isLoading && (
          <div className="flex-1 flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-hero-red" />
              <p className="text-[13px] text-text-grey">Loading Weld Parameters...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {!isLoading && error && (
          <div className="flex-1 flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3 max-w-md">
              <div className="rounded-full bg-[rgba(196,0,31,0.06)] p-3">
                <svg className="h-6 w-6 text-readable-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-[14px] font-semibold text-blueprint-blue">Error Loading Parameters</h3>
              <p className="text-center text-[13px] text-text-grey">{error}</p>
              <Button onClick={refresh} variant="outline" className="h-9 px-4 border-[#E2E5EA]">
                Retry
              </Button>
            </div>
          </div>
        )}

        {/* No Data State */}
        {!isLoading && !error && !currentMachineType && (
          <div className="flex-1 flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <div className="rounded-full bg-page-bg p-3">
                <svg className="h-6 w-6 text-heritage-grey" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h3 className="text-[14px] font-semibold text-blueprint-blue">No Weld Parameters Found</h3>
              <p className="text-center text-[13px] text-text-grey">
                No weld parameters data available for this project.
              </p>
            </div>
          </div>
        )}

        {/* Content - P625 */}
        {!isLoading && !error && currentMachineType === 'p625' && (
          <div className="flex-1 overflow-hidden">
            {selectedJobData ? (
              <P625Parameters 
                key={`p625-${selectedJobData.job_number || 'default'}`}
                jobData={[selectedJobData]} 
                projectId={projectId} 
                onRenameSuccess={refresh}
                canEdit={canEdit}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-4">
                  <p className="text-lg font-medium text-gray-700">No P625 data available</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Content - M500 */}
        {!isLoading && !error && currentMachineType === 'm500' && (
          <div className="flex-1 overflow-hidden">
            {selectedJobData ? (
              <M500Parameters 
                key={`m500-${selectedJobData.job_number || 'default'}`}
                jobData={[selectedJobData]} 
                projectId={projectId} 
                onRenameSuccess={refresh}
                canEdit={canEdit}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-4">
                  <p className="text-lg font-medium text-gray-700">No M500 data available</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Content - P600z */}
        {!isLoading && !error && currentMachineType === 'p600z' && (
          <div className="flex-1 overflow-hidden">
            {selectedJobData ? (
              <P600zParameters 
                key={`p600z-${selectedJobData.job_number || 'default'}`}
                jobData={[selectedJobData]} 
                projectId={projectId} 
                onRenameSuccess={refresh}
                canEdit={canEdit}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-4">
                  <p className="text-lg font-medium text-gray-700">No P600z data available</p>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CommonWeldParametersModal;

